import type { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import { Appearance } from "common/generator/components/button-appearance";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { useLayer } from "react-laag";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
import { APP_MODAL_ROOT } from "webapp/lib/constants";
import type { ExtractedActions } from "webapp/wire-renderers/wire-lib";
import { breakoutActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import { WireButton } from "../wire-button/wire-button";
import { WireMenuButton } from "../wire-menu-button/wire-menu-button";

interface FilterMenuProps {
    filterValue?: string;
    filterItems?: readonly (string | undefined)[];
    onFilterChange?: (newIndex: number) => void;
    appKind: AppKind;
}

const WireFilterMenu: GlideFC<FilterMenuProps> = p => {
    const { filterItems, filterValue, onFilterChange, appKind } = p;

    const [open, setOpen] = React.useState(false);

    const { layerProps, triggerProps, renderLayer } = useLayer({
        isOpen: open,
        auto: true,
        placement: "bottom-end",
        possiblePlacements: ["top-end", "top-start", "bottom-end", "bottom-start"],
        container: APP_MODAL_ROOT,
        triggerOffset: 5,
        onOutsideClick: () => setOpen(false),
    });

    if (onFilterChange === undefined || filterItems === undefined) return null;
    return (
        <button
            {...triggerProps}
            onClick={() => setOpen(cv => !cv)}
            tw="bg-bg-front flex flex-1 text-icon-base items-center justify-between h-9 ml-2 px-3 shadow-sm transition
                rounded-lg overflow-hidden first:ml-0 min-width[98px] hover:(bg-bg-middle shadow-sm-dark
                text-icon-base) focus:(ring-2 ring-accent)">
            <div
                css={[filterValue === undefined && tw`text-text-disabled!`]}
                tw="overflow-ellipsis flex-shrink min-w-0 whitespace-nowrap overflow-hidden leading-none">
                {filterValue ?? getLocalizedString("filterBy", appKind) + "..."}
            </div>
            <GlideIcon iconSize={16} kind="stroke" icon="st-caret" tw="ml-2 flex-shrink-0" />
            {open &&
                renderLayer(
                    <ul
                        tw="max-width[300px] min-width[192px] flex flex-col bg-bg-front rounded-lg py-2 ml-2
                            shadow-md-dark"
                        {...layerProps}>
                        {filterItems.map((item, i) => (
                            <li
                                key={i}
                                tw="px-3 py-2 cursor-pointer text-sm text-text-dark hover:bg-bg-back
                                    focus-visible:underline"
                                onClick={() => onFilterChange?.(i)}>
                                {item ?? "All"}
                            </li>
                        ))}
                    </ul>
                )}
        </button>
    );
};

interface Props extends FilterMenuProps {
    title: string;
    searchValue?: string;
    onSearchChange?: (newVal: string) => void;
    titleActions: ExtractedActions;
}

export const WireListContainer: GlideFC<React.PropsWithChildren<Props>> = p => {
    const { title, children, onSearchChange, searchValue, titleActions, appKind } = p;

    const [firstAction, secondAction, ...menuActions] = breakoutActions(titleActions, 2);

    const hasSearch = onSearchChange !== undefined;
    const hasActions = firstAction !== undefined || secondAction !== undefined || menuActions.length > 0;

    return (
        <div tw="flex flex-col">
            <div
                tw="flex flex-wrap flex-col items-start mb-3
                    gp-md:flex-row
                    gp-md:items-center
                    gp-lg:mb-4">
                {title && <h2 tw="text-2xl text-text-contextual-dark font-extrabold mr-2 mb-2">{title}</h2>}
                <div tw="gp-sm:flex-1" />
                <div
                    tw="w-full flex flex-1 flex-col flex-wrap
                        gp-md:(flex-initial flex-nowrap flex-row)
                        gp-lg:w-auto">
                    <div
                        tw="flex order-last
                            gp-md:(mt-0 order-none)">
                        {hasSearch && (
                            <label
                                tw="shadow-sm transition rounded-lg bg-bg-front mb-2 h-9 px-3 text-icon-xpale flex
                                    flex[2 1 0%] min-width[156px] items-center hover:(shadow-sm-xdark text-icon-pale)
                                    focus-within:(ring-2 ring-accent text-icon-base)">
                                <GlideIcon iconSize={16} kind="stroke" icon="st-search" tw="mr-2 flex-shrink-0" />
                                <input
                                    tw="text-text-base leading-none min-width[60px] w-full flex-shrink"
                                    placeholder={getLocalizedString("search", appKind) + "..."}
                                    value={searchValue}
                                    onChange={e => onSearchChange(e.target.value)}
                                />
                            </label>
                        )}
                        <WireFilterMenu {...p} />
                    </div>
                    {hasActions && (
                        <div
                            tw="flex flex-1 items-center w-full min-w-0 ml-0 mb-3 space-x-2
                                gp-md:(mb-2 ml-2)">
                            {firstAction !== undefined && (
                                <WireButton {...makeActionSpreadProps(firstAction)} appearance={Appearance.Filled} />
                            )}
                            {secondAction !== undefined && (
                                <WireButton {...makeActionSpreadProps(secondAction)} appearance={Appearance.Bordered} />
                            )}
                            {menuActions.length > 0 && (
                                <WireMenuButton appearance={Appearance.Bordered} menuItems={menuActions} />
                            )}
                        </div>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
};
